/**
 * The keyboard half of a radio group, for the ones the shell draws itself
 * (`SegmentedControl`, `RadioCards`, `ChoiceTiles`, `OpponentLadder`).
 *
 * The WAI-ARIA radio-group pattern: the group is one Tab stop (a roving
 * `tabindex`: the checked option is 0, the others -1), and inside it the
 * arrow keys move to the next or previous option *and select it*, wrapping at
 * either end; Home and End go to the first and the last.
 *
 * Since 1.5.0 a disabled option is stepped over (`isDisabled`): an arrow goes
 * on to the next one that can be chosen, in its direction and wrapping; Home
 * and End go to the first and the last that can be. With none that can, no
 * key is the group's. (Before, an arrow onto a disabled option chose nothing
 * and moved nothing, so the keys were stuck behind it.)
 *
 * Pure, so the arithmetic is tested without a DOM.
 */

/** Whether the option at an index cannot be chosen. */
export type RadioDisabled = (index: number) => boolean;

const never: RadioDisabled = () => false;

/** The first index from `start`, stepping by `step` with wrap, that is not disabled. */
function nextEnabled(
  start: number,
  step: 1 | -1,
  count: number,
  isDisabled: RadioDisabled,
): number | null {
  for (let tried = 0, index = start; tried < count; tried += 1) {
    if (!isDisabled(index)) return index;
    index = (index + step + count) % count;
  }
  return null;
}

/** The index a key moves a radio group to, or `null` when the key is not the group's. */
export function radioKeyTarget(
  key: string,
  current: number,
  count: number,
  isDisabled: RadioDisabled = never,
): number | null {
  if (count <= 0) return null;
  // Nothing checked yet: forwards starts at the first, backwards at the last.
  const from = current >= 0 && current < count ? current : null;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return nextEnabled(from === null ? 0 : (from + 1) % count, 1, count, isDisabled);
    case 'ArrowLeft':
    case 'ArrowUp':
      return nextEnabled(
        from === null ? count - 1 : (from - 1 + count) % count,
        -1,
        count,
        isDisabled,
      );
    case 'Home':
      return nextEnabled(0, 1, count, isDisabled);
    case 'End':
      return nextEnabled(count - 1, -1, count, isDisabled);
    default:
      return null;
  }
}

/**
 * The one option that is the group's Tab stop: the checked one, or the first
 * when none is. Since 1.7.0, given `isDisabled` and `count`, the first that
 * can be chosen when none is checked (a disabled option is not focusable, so
 * a group whose first option is disabled had no Tab stop at all).
 */
export function rovingTabIndex(
  index: number,
  checkedIndex: number,
  isDisabled: RadioDisabled = never,
  count = index + 1,
): 0 | -1 {
  if (checkedIndex >= 0) return index === checkedIndex ? 0 : -1;
  return index === (nextEnabled(0, 1, Math.max(count, 1), isDisabled) ?? 0) ? 0 : -1;
}

/**
 * Handles a keydown on a radio inside `group`: selects the option the key
 * moves to and puts focus on it. Returns whether the key was the group's.
 * A key held with Ctrl, Meta or Alt is never the group's. `isDisabled`
 * (since 1.5.0) names the options to step over.
 */
export function handleRadioKeydown(
  event: KeyboardEvent,
  group: HTMLElement | null | undefined,
  checkedIndex: number,
  count: number,
  select: (index: number) => void,
  isDisabled: RadioDisabled = never,
): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  const target = radioKeyTarget(event.key, checkedIndex, count, isDisabled);
  if (target === null) return false;
  event.preventDefault();
  select(target);
  group?.querySelectorAll<HTMLElement>('[role="radio"]')[target]?.focus();
  return true;
}
